<script>
	import { onMount } from "svelte";

	// Props
	export let placeholder = "Select...";
	export let disabled = false; // disable the select
	export let error = false;
	export let errorText = "";
	export let showNavArrows = true;
	export let options = []; // all available options from parent
	export let value = null; // currently selected option
	export let onChange = () => {}; // when an item is selected
	export let onLoadMore = () => {}; // infinite scroll trigger
	export let changeIdx = () => {}; // function to change index in parent
	export let changeReportIdx = () => {}; // function to change report index in parent
	export let showCopyButton = false;
	export let selectedLabelKey = "";
	export let changeImageIdx = () => {}; // function to change image index in parent

	let isOpen = false;
	let visibleOptions = [];
	let startIndex = 0;
	const loadCount = 10;

	// reactive index based on value prop
	$: currentReportIdx = value
		? options.findIndex((o) => o.value === value.value)
		: -1;

	// Computed properties for navigation (Prev and Next button states)
	$: isPrevBtnDisabled =
		!value ||
		options.length === 0 ||
		currentReportIdx === options.length - 1;
	$: isNextBtnDisabled = !value || currentReportIdx <= 0;

	onMount(() => {
		loadInitial();
	});

	function loadInitial() {
		visibleOptions = options.slice(0, loadCount);
		startIndex = loadCount;
	}

	// When options grow (e.g., after scroll load), update visibleOptions
	$: if (options.length > visibleOptions.length) {
		visibleOptions = options.slice(0, startIndex);
	}

	function toggleDropdown() {
		if (!disabled) isOpen = !isOpen;
	}

	function selectOption(option) {
		onChange(option); // emit selection
		isOpen = false;
	}

	let isLoadingMore = false;
	function handleScroll(e) {
		const { scrollTop, scrollHeight, clientHeight } = e.target;
		if (!isLoadingMore && scrollTop + clientHeight >= scrollHeight - 10) {
			isLoadingMore = true;
			onLoadMore();
			startIndex += loadCount;
			setTimeout(() => {
				isLoadingMore = false;
			}, 300);
		}
	}

	function handleNext() {
		if (currentReportIdx > 0) {
			onChange(options[currentReportIdx - 1]);
		}
		changeImageIdx(0);
	}

	function handlePrev() {
		if (currentReportIdx < options.length - 1) {
			onChange(options[currentReportIdx + 1]);
		}
		changeImageIdx(0);
	}

	async function handleCopy() {
		if (!value.label || typeof navigator === "undefined") return;
		await navigator.clipboard.writeText(value[selectedLabelKey]);
	}
</script>

<div class="select-wrapper">
	{#if showNavArrows}
		<button
			class="arrow-button"
			on:click={handlePrev}
			disabled={isPrevBtnDisabled}
		>
			<img src="/assets/icon-prev-arrow.svg" alt="Previous report" />
			<span>Prev</span>
		</button>
	{/if}

	<div class="d-flex">
		{#if showCopyButton && value}
			<button
				class="icon-button"
				on:click|stopPropagation={handleCopy}
				aria-label="Copy text"
			>
				<img src="/assets/icon-copy.svg" alt="Copy" />
			</button>
		{/if}

		<button class="dropdown" on:click={toggleDropdown}>
			{#if value}
				{#if selectedLabelKey && value[selectedLabelKey]}
					{value[selectedLabelKey]}
				{:else}
					{value.label.split("–")[0].trim()}
				{/if}
			{:else}
				<span>{placeholder}</span>
			{/if}

			<img
				src={`/assets/icon-chevron-${isOpen ? "up" : "down"}.png`}
				width="18"
				height="18"
				alt={isOpen ? "Chevron up icon" : "Chevron down icon"}
			/>
		</button>
	</div>
	{#if isOpen}
		<div class="dropdown-list" on:scroll={handleScroll}>
			{#each visibleOptions as opt}
				<button class="option" on:click={() => selectOption(opt)}>
					{opt.isLastReviewed ? "(Last Reviewed) " : ""}{opt.label}
				</button>
			{/each}
		</div>
	{/if}

	{#if showNavArrows}
		<button
			class="arrow-button"
			on:click={handleNext}
			disabled={isNextBtnDisabled}
		>
			<span>Next</span>
			<img src="/assets/icon-next-arrow.svg" alt="Next report" />
		</button>
	{/if}

	{#if error}
		<small class="error-text">{errorText}</small>
	{/if}
</div>

<style>
	.select-wrapper {
		display: grid;
		grid-template-columns: auto 1fr auto;
		align-items: center;
		gap: 12px;
		flex-wrap: wrap;
		position: relative;
		width: 100%;
	}

	.select-wrapper > .d-flex {
		justify-content: center;
	}

	.dropdown {
		all: unset;
		box-sizing: border-box;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
		padding: 12px 16px;
		font-size: 24px;
		font-weight: 700;
		cursor: pointer;
		min-width: 25%;
		background-color: transparent;
		user-select: none;
		-webkit-tap-highlight-color: transparent;
		-webkit-user-select: none;
	}

	.option {
		all: unset;
		box-sizing: border-box;
		display: block;
		width: 100%;
		padding: 10px 16px;
		cursor: pointer;
		white-space: nowrap;
		border-bottom: 1px solid #f0f0f0;
		text-align: left;
		font-size: 14px;
	}

	.option:hover {
		background-color: #f6f6f6;
	}

	.dropdown:active,
	.option:active {
		background-color: transparent;
	}

	.dropdown-list {
		position: absolute;
		top: 100%;
		left: 50%;
		transform: translateX(-50%);
		max-height: 200px;
		overflow-y: auto;
		background: white;
		border: 1px solid #ccc;
		border-radius: 8px;
		margin-top: 4px;
		z-index: 10;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
	}

	.arrow {
		margin-left: 8px;
		transition: transform 0.2s;
		display: inline-block;
		width: 16px;
	}

	.arrow-button {
		background: none;
		border: none;
		font-size: 14px;
		cursor: pointer;
		color: #000;
		display: inline-flex;
		align-items: center;
		gap: 6px;
	}

	.icon-button {
		border: none;
		background: transparent;
		padding: 0;
		cursor: pointer;
		display: inline-flex;
		align-items: center;
		justify-content: center;
	}

	.icon-button img {
		width: 20px;
		height: 20px;
	}

	.arrow-button:disabled {
		color: #aaa;
		cursor: not-allowed;
	}

	.arrow-button:disabled img {
		opacity: 0.45;
		filter: grayscale(100%);
	}

	.error-text {
		color: red;
		font-size: 0.85rem;
		margin-top: 4px;
	}
</style>
